import { useEffect, useRef } from "react"
import {
  loadViewMediaScript,
  ANALYTIC_TYPE_TO_EVENT,
  PLAYER_STATE_TO_EVENT,
} from "../utils/viewMediaPlayer"
import { playerEventTypes } from "../constants"
import { createId } from "../utils/visitor"

const STATE_POLL_INTERVAL_MS = 2000
const BOOT_FALLBACK_DELAY_MS = 1500
const API_CALL_TIMEOUT_MS = 2000

// The branding intro runs 3-4 s; every real video is far longer. Anything
// shorter than this is treated as the intro whatever the item lookup says.
const INTRO_MAX_SECONDS = 10
// A jump back to the start only begins a new play once the current play got
// this far — earlier rewinds are part of the same play.
const REPLAY_AFTER_PROGRESS = 0.9
// "Back at the start" means within this many seconds of 0.
const RESTART_WINDOW_SECONDS = 3

const PLAYER_STATE_PLAYING = 1
// Lookups per item change before relying on the duration alone.
const MAX_ITEM_LOOKUPS = 3

const withTimeout = (promise, ms = API_CALL_TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms)
    ),
  ])

// ViewMedica's own analytics payload sends percent as "50%", not 50 — strip
// anything that isn't part of the number before it reaches the API, which
// expects a clean number.
const toNumber = (value) => {
  if (value === undefined || value === null) return undefined
  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""))
  return Number.isNaN(parsed) ? undefined : parsed
}

// The analytics payload carries an `item`; its exact shape isn't documented,
// so accept either a file string or an object with `file`.
const itemFile = (item) => (typeof item === "string" ? item : item?.file)

/*
  Wires one <iframe> playing a ViewMedica video up to the two event channels
  the vendor exposes: the analytics postMessage stream, and state polling for
  play/pause/waiting transitions ViewMedica does not push on its own. Events
  go to the page's queue (usePlayerEventQueue), never straight to the API.

  Intro filtering. Before the real ("primary") video, the player runs a short
  branding intro in the same iframe that fires its own analytics. Whether the
  main video is on screen is decided from state the poll keeps up to date —
  never by awaiting the player per event, which raced the intro-to-main switch
  and dropped events on a timeout.

  Play-throughs. Each play of the main video gets its own playId, so the
  admin side can count how many times a video was played. A new play starts
  on the first play, after the previous one ended, when the intro runs again,
  or on a jump back to the start once the previous play passed 90%. Events
  outside a play (the intro, or after a play ended until the next one starts)
  are not sent.

  `enabled: false` (admin preview opens) no-ops entirely — no script load, no
  listener, no polling, no events sent.
*/
export const useVideoPlaybackTracking = ({
  iframeRef,
  videoId,
  source,
  trackingIds,
  enabled,
  enqueueEvent,
}) => {
  const enqueueEventRef = useRef(enqueueEvent)
  enqueueEventRef.current = enqueueEvent

  useEffect(() => {
    if (!enabled || !videoId || !iframeRef.current) return

    const iframe = iframeRef.current
    let cancelled = false
    let pollIntervalId = null
    let api = null

    // Cached player facts, refreshed by the poll. null = not known yet.
    let primaryItem = null
    let primaryItemAttempts = 0
    let currentIsPrimary = null
    let lastDuration = null
    // The duration the item was last looked up for, and how many lookups
    // that duration has had (see refreshItemState).
    let itemCheck = { duration: undefined, attempts: 0 }

    // The open play, or null between plays.
    let play = null
    // The intro played after the main video had — the start of a replay.
    let introSeenAgain = false

    // Duration first: getCurrentItem() is at its least reliable during the
    // intro, but the intro's length gives it away. Until the item lookups
    // answer, length alone decides.
    const isMainVideo = () => {
      const hasDuration = Number.isFinite(lastDuration) && lastDuration > 0
      if (hasDuration && lastDuration < INTRO_MAX_SECONDS) return false
      if (currentIsPrimary !== null) return currentIsPrimary
      return hasDuration
    }

    // Whether the analytics event's own item can be compared with the
    // primary — the only answer that can't be stale.
    const canCompareItem = (item) => !!(itemFile(item) && primaryItem?.file)

    // Prefer the item the analytics event names, which arrives with the event
    // itself; fall back to the cached state when it can't be compared.
    const isMainVideoItem = (item) =>
      canCompareItem(item) ? itemFile(item) === primaryItem.file : isMainVideo()

    const openPlay = () => {
      play = {
        playId: createId(),
        maxProgress: 0,
        ended: false,
        watchedSeconds: 0,
        milestones: new Set(),
      }
      introSeenAgain = false
    }

    // Only the player's own video_ended ends a play: that event is what the
    // admin side counts as a finished play, so nothing may block it. A replay
    // without one is still caught by the intro or back-to-the-start rules.
    const recordProgress = (progress) => {
      if (!play || !Number.isFinite(progress)) return
      play.maxProgress = Math.max(play.maxProgress, progress)
    }

    // Called when the main video is seen playing. vm.js reports a time of 0
    // as undefined, so undefined counts as "at the start". After video_ended
    // the player may still read "playing" on the end screen, so even then a
    // new play needs the time back at the start.
    const shouldOpenPlay = (currentTime) => {
      if (!play || introSeenAgain) return true
      const atStart =
        currentTime === undefined || currentTime < RESTART_WINDOW_SECONDS
      if (play.ended) return atStart
      return atStart && play.maxProgress >= REPLAY_AFTER_PROGRESS
    }

    const send = (eventType, extra = {}) => {
      enqueueEventRef.current?.({
        videoId,
        source,
        eventType,
        playId: play?.playId ?? null,
        ...trackingIds,
        ...extra,
      })
    }

    const handleMessage = (event) => {
      if (!event.origin || !event.origin.match(/ondemand\.viewmedica\.com/i)) {
        return
      }
      // A report can embed several videos, each with its own iframe/message
      // stream — without this check, one video's events could be attributed
      // to another.
      if (event.source !== iframe.contentWindow) return

      const { analyticType, payload } = event.data || {}
      if (analyticType === undefined) return

      const eventType = ANALYTIC_TYPE_TO_EVENT[analyticType]
      if (!eventType) return

      // Printing the brochure isn't part of watching, so it has no play.
      if (eventType === playerEventTypes.BROCHURE_PRINTED) {
        send(eventType, { playId: null, label: payload?.label })
        return
      }

      if (!isMainVideoItem(payload?.item)) return

      // Faster than waiting for the poll to see the player running, but only
      // when the event's own item proves it is the main video: on a replay the
      // intro's video_started arrives before the poll has noticed the intro,
      // and the cached state would open a play for it. Not used for the "back
      // to the start" rule either — the player also fires this on a seek
      // backwards, with no time to tell the two apart.
      if (
        eventType === playerEventTypes.VIDEO_STARTED &&
        canCompareItem(payload?.item) &&
        (!play || play.ended || introSeenAgain)
      ) {
        openPlay()
      }

      if (!play || play.ended) return

      const percent = toNumber(payload?.percent)
      if (eventType === playerEventTypes.PERCENT_REACHED) {
        // The player can report the same milestone twice in one play.
        if (percent === undefined || play.milestones.has(percent)) return
        play.milestones.add(percent)
      }

      send(eventType, {
        percent,
        // A step's title ("Introduction", "Symptoms", ...), not a numeric
        // index — sent through as-is, same as label.
        step: payload?.step,
        label: payload?.label,
      })

      if (eventType === playerEventTypes.PERCENT_REACHED) {
        recordProgress(percent / 100)
      }
      if (eventType === playerEventTypes.VIDEO_ENDED) play.ended = true
    }

    window.addEventListener("message", handleMessage)

    /*
      The item lookups are rationed. Inside ViewMedica's iframe,
      getCurrentItem() throws whenever there is no current item (logged there
      as '"undefined" is not valid JSON') and then never answers, so each
      failed call costs a full timeout and a console error. The current item
      only changes when the duration does (intro <-> main video), so it is
      only asked again after the duration changes, a few times at most. If it
      never answers, isMainVideo() falls back to the duration alone.

      A timed-out or empty lookup keeps the previous answer rather than
      flipping to "not the main video" and dropping events.

      The throw happens in the vendor's window, so no catch on this side can
      stop it from being logged — the only fix is not to ask. With no video
      loaded there is no item to ask about, and the duration (NaN or 0, and it
      can flip between the two) would keep resetting the cap above, so nothing
      is looked up until the player reports a real duration.
    */
    const refreshItemState = async () => {
      if (!(Number.isFinite(lastDuration) && lastDuration > 0)) return

      if (!primaryItem && primaryItemAttempts < MAX_ITEM_LOOKUPS) {
        primaryItemAttempts += 1
        primaryItem = await withTimeout(api.getPrimaryItem()).catch(() => null)
      }
      if (!primaryItem?.file) return

      // An answer only holds for the item it was given about: once the
      // duration changes, the duration decides until a new answer arrives.
      if (!Object.is(itemCheck.duration, lastDuration)) {
        itemCheck = { duration: lastDuration, attempts: 0 }
        currentIsPrimary = null
      }
      if (itemCheck.attempts >= MAX_ITEM_LOOKUPS) return
      itemCheck.attempts += 1

      const currentItem = await withTimeout(api.getCurrentItem()).catch(
        () => null
      )
      if (currentItem?.file) {
        currentIsPrimary = currentItem.file === primaryItem.file
        // Answered — nothing more to ask until the duration changes.
        itemCheck.attempts = MAX_ITEM_LOOKUPS
      }
    }

    const startStatePolling = () => {
      let lastState = null
      let lastTime = null
      // A tick can outlast the interval while a player call waits out its
      // timeout; two ticks at once would interleave lastState/lastTime.
      let polling = false

      pollIntervalId = setInterval(async () => {
        if (document.hidden || polling) return
        polling = true

        try {
          const [state, currentTime, duration] = await Promise.all([
            withTimeout(api.getPlayerState()),
            withTimeout(api.getCurrentTime()),
            withTimeout(api.getDuration()),
          ])
          lastDuration = duration
          await refreshItemState()
          if (cancelled) return

          const isMain = isMainVideo()
          const isPlaying = state === PLAYER_STATE_PLAYING

          if (isPlaying && !isMain && play) introSeenAgain = true
          // The intro usually runs straight into the main video, so the state
          // never changes from "playing" — a new play still reports it.
          const playOpened = isPlaying && isMain && shouldOpenPlay(currentTime)
          if (playOpened) openPlay()

          if (play && isMain) {
            if (
              isPlaying &&
              lastState === PLAYER_STATE_PLAYING &&
              lastTime !== null &&
              currentTime > lastTime
            ) {
              play.watchedSeconds += currentTime - lastTime
            }
            if (Number.isFinite(duration) && duration > 0) {
              recordProgress((currentTime ?? 0) / duration)
            }
          }

          if (
            (state !== lastState || playOpened) &&
            isMain &&
            play &&
            !play.ended
          ) {
            const eventType = PLAYER_STATE_TO_EVENT[state]
            if (eventType) {
              send(eventType, {
                currentTime,
                duration,
                watchedSeconds: Math.round(play.watchedSeconds),
              })
            }
          }

          lastState = state
          lastTime = currentTime
        } catch {
          // Tracking must never interrupt playback — a failed poll is simply
          // skipped, the same as a failed event send.
        } finally {
          polling = false
        }
      }, STATE_POLL_INTERVAL_MS)
    }

    const boot = () => {
      if (cancelled) return
      api = new window.OndemandPlayerApi({ frame: iframe })
      startStatePolling()
    }

    let booted = false
    const bootOnce = () => {
      if (booted) return
      booted = true
      boot()
    }

    loadViewMediaScript()
      .then(() => {
        if (cancelled) return
        iframe.addEventListener("load", bootOnce)
        // Fallback in case the iframe's "load" already fired before this
        // listener attached.
        setTimeout(bootOnce, BOOT_FALLBACK_DELAY_MS)
      })
      .catch((error) => {
        console.warn("Failed to load ViewMedica player script:", error)
      })

    return () => {
      cancelled = true
      window.removeEventListener("message", handleMessage)
      iframe.removeEventListener("load", bootOnce)
      if (pollIntervalId) clearInterval(pollIntervalId)
    }
  }, [enabled, videoId, source, trackingIds, iframeRef])
}
